/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, ProgressIndicator } from './ProgressIndicator';
import { helpRenders } from './ProgressIndicator_Help';
import { CommonProps } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';

export const ProgressIndicatorProps: PropertyDefinitions<
  Omit<ProgressIndicator, keyof CommonProps>
> = {
  type: {
    label: 'Style',
    type: 'select',
    options: toOptions(['circular', 'linear']),
    category: 'Look & Feel',
  },
  thickness: {
    label: 'Thickness',
    type: 'number',
    max: 10,
    min: 1,
    category: 'Look & Feel',
  },
  size: {
    label: 'Size',
    type: 'number',
    max: 140,
    min: 40,
    step: 4,
    category: 'Look & Feel',
  },
  linearWidth: {
    label: 'Width',
    type: 'number',
    max: 400,
    min: 80,
    step: 4,
    category: 'Look & Feel',
  },
  color: {
    label: 'Color',
    type: 'ColorExpression',
    helpText:
      'e.g. primary.main or primary.dark or primary.light or any valid CSS color string',
    category: 'Look & Feel',
  },
  bgcolor: {
    label: 'Background Color',
    type: 'ColorExpression',
    helpText: 'e.g. divider or primary.light or any valid CSS color string',
    category: 'Look & Feel',
  },
  fontSize: {
    label: 'Font Size',
    type: 'string',
    category: 'Look & Feel',
  },
  viewAttribute: {
    label: 'View Attribute',
    type: 'ViewAttributeChooser',
    category: 'Data',
    aliasType: 'STORE',
    getAttributeDataTypes: () => ['Percent', 'Double', 'Integer', 'Decimal'],
    preventMultiEdit: true,
    helpText:
      'The value must be a percent between zero & one. A value of 1 will be treated as 100%. Any value greater than 1 will be displayed as is.',
  },
  ...toolTipProps,
};

const ProgressIndicatorManager: ComponentManagerFn<'ProgressIndicator'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ProgressIndicator'>(platform, 'ProgressIndicator'),
    type: 'ProgressIndicator',
    name: 'Progress Indicator',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...ProgressIndicatorProps,
    },
    isPropertyVisible(prop, { type }): boolean {
      switch (prop) {
        case 'size':
        case 'thickness':
        case 'fontSize':
          return type === 'circular';
        case 'linearWidth':
          return type === 'linear';
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Data',
    icon: { iconName: 'percent' },
    description: `**Overview**<br/>
The Progress Indicator is a data-driven visual component used to display the completion status of a task, process, or capacity metric. It translates raw numbers (e.g., 0.75 or 75) into an instantly readable graphical representation—either a circular ring or a linear bar.
Unlike a generic "loading spinner" which implies an indeterminate wait time, this component is designed to show determinate progress based on real values from your database.

**1. Data Binding (The Source)**<br/>
The core function of this component is to visualize a numeric value.
* **View Attribute:** Select the data field that holds the progress value. (Supported Types: Percent, Decimal, Double, Integer).
* **Interpretation Rules:**
    * **0 to 1:** Treated as a percentage ratio (e.g., 0.45 = 45%).
    * **1:** Treated as 100%.
    * **> 1:** Treated as a raw percentage (e.g., 75 = 75%).

**2. Visual Style**<br/>
You can choose between two distinct visualization metaphors depending on your layout needs.
* **Style (Type):**
    * **Circular:** Renders a ring. Best for "Badges," "Scorecards," or compact spaces where the vertical and horizontal footprints are equal. (Customization: Control Size and Thickness).
    * **Linear:** Renders a horizontal bar. Best for "Steps," "Timelines," or areas with wide horizontal space but limited vertical height. (Customization: Control Width).



**3. Colors & Context**<br/>
Use color to add semantic meaning to the progress (e.g., Green for "Good/Complete," Red for "Critical/Stuck").
* **Color:** The fill color of the progress bar itself.
* **Background Color:** The color of the "empty" track behind the progress bar.
* **Note:** Both color fields support Expressions, allowing you to dynamically change color based on the value (e.g., turn red if progress < 20%).

**4. Interaction**<br/>
Although primarily a read-only display, the component can be interactive.
* **TriggerOnClick:** You can add this child component to execute a workflow when the user clicks the progress bar. (Use Case: Click a "75% Complete" project indicator to navigate to the detailed project tasks view).

**5. Best Practices**<br/>
* **Context:** Always ensure the user understands what the progress represents. Use a tooltip or a nearby label to clarify (e.g., "Storage Used" vs. "Task Completion").
* **Precision:** If the exact number is critical, ensure the Font Size is readable or consider pairing this component with a standard Text component.`,
    helpRenders,
  };
};

export default ProgressIndicatorManager;
